import L from "leaflet";
import * as turf from "@turf/turf";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import JSZip from "jszip";

export const crossSectionTool = {
  id: "cross-section",
  name: "Cross Sections",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, containerEl) { if (!this._rt) this._rt = createRuntime(app); this._rt.mount(containerEl); },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) { if (!this._rt) this._rt = createRuntime(app); this._rt.restore?.(data); },
};

function createRuntime(app) {
  const map = app.map.instance;
  const mapEl = app.map.el;
  const root = L.featureGroup().addTo(map);

  let container = null;
  let drawSession = null;
  let globalLayerId = null;
  let inGlobalSync = false;
  let globalVisible = true;
  let globalOpacity = 1;

  let centerline = null;
  let sections = [];
  let profilesBySectionId = new Map();
  let currentSectionIdx = -1;
  let seq = 1;
  let statusText = "idle";
  let progressValue = 0;
  let progressText = "Idle";
  let profilePlot = null;
  let hoverMarker = null;
  let bottomMounted = false;
  let prefetchRunId = 0;
  let sessionToken = 0;
  const pendingProfiles = new Map();

  const state = {
    sectionSpacingM: 50,
    leftM: 50,
    rightM: 50,
    sampleSpacingM: 5,
    skipLand: true,
    autoSampleFirst: true,
    exportMode: "csv_current",
  };

  function esc(v) {
    return String(v ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
  }

  function uid(prefix) {
    seq += 1;
    return `${prefix}_${Date.now().toString(36)}_${seq.toString(36)}`;
  }

  function setStatus(msg) {
    statusText = String(msg || "");
    const el = container?.querySelector?.("#csx-status");
    if (el) el.textContent = statusText;
    const b = document.getElementById("csx-view-status");
    if (b) b.textContent = statusText;
  }

  function setCount() {
    const b = document.getElementById("csx-view-count");
    if (b) b.textContent = `${sections.length} sections`;
  }

  function ensureBottomPanel() {
    if (bottomMounted) return;
    app.ui.mountBottomPanel(`
      <div class="card" id="csx-bottom-card" style="height:100%; display:flex; flex-direction:column;">
        <div style="font-weight:700;">Cross Section Profile</div>
        <div class="row" style="margin-top:6px; gap:8px; flex-wrap:wrap;">
          <button id="csx-view-prev" class="csx-nav-btn" title="Previous section" aria-label="Previous section">&lt;</button>
          <select id="csx-view-select" style="min-width:120px;"><option value="">Select section</option></select>
          <button id="csx-view-next" class="csx-nav-btn" title="Next section" aria-label="Next section">&gt;</button>
          <select id="csx-view-export-mode" style="min-width:170px;">
            <option value="csv_current">CSV (Current Section)</option>
            <option value="png_current">PNG (Current Section)</option>
            <option value="png_all_zip">PNG ZIP (All Sections)</option>
            <option value="pdf_all_qc">QC PDF (All Sections)</option>
          </select>
          <button id="csx-view-export-run" class="btn">Export</button>
          <span id="csx-view-count" class="small">0 sections</span>
          <span id="csx-view-status" class="small" style="margin-left:auto;">idle</span>
        </div>
        <div style="margin-top:6px;">
          <div style="height:8px; border:1px solid rgba(56,189,248,.35); border-radius:999px; overflow:hidden; background:rgba(15,23,42,.9);">
            <div id="csx-view-progress-bar" style="height:100%; width:0%; background:linear-gradient(90deg,#0ea5e9,#22d3ee,#22c55e); transition:width .18s ease;"></div>
          </div>
          <div id="csx-view-progress-label" class="small muted" style="margin-top:3px;">Idle</div>
        </div>
        <div class="row" style="margin-top:6px; gap:8px;">
          <span id="csx-view-label" class="small" style="margin-left:auto;">No section selected</span>
        </div>
        <div id="csx-profile" style="margin-top:6px; flex:1; min-height:0;"><div class="small">No profile samples yet.</div></div>
      </div>
    `, {
      ownerId: "cross-section",
      title: "Cross Section",
      onShow: () => {
        bindBottomControls();
        syncBottomControls();
      },
    });
    bottomMounted = true;
  }

  function bindBottomControls() {
    const prev = document.getElementById("csx-view-prev");
    const next = document.getElementById("csx-view-next");
    const sel = document.getElementById("csx-view-select");
    const expMode = document.getElementById("csx-view-export-mode");
    const expRun = document.getElementById("csx-view-export-run");
    if (prev) {
      prev.onclick = () => {
        if (!sections.length) return;
        const i = currentSectionIdx <= 0 ? sections.length - 1 : currentSectionIdx - 1;
        showSection(i);
      };
    }
    if (next) {
      next.onclick = () => {
        if (!sections.length) return;
        const i = (currentSectionIdx + 1) % sections.length;
        showSection(i);
      };
    }
    if (sel) {
      sel.onchange = () => {
        const i = Number(sel.value);
        if (Number.isInteger(i) && i >= 0 && i < sections.length) showSection(i);
      };
    }
    if (expMode) {
      expMode.onchange = () => {
        state.exportMode = expMode.value || "csv_current";
        syncBottomControls();
      };
    }
    if (expRun) expRun.onclick = () => runSelectedExport();
  }

  function syncBottomControls() {
    const prev = document.getElementById("csx-view-prev");
    const next = document.getElementById("csx-view-next");
    const sel = document.getElementById("csx-view-select");
    const expMode = document.getElementById("csx-view-export-mode");
    const expRun = document.getElementById("csx-view-export-run");
    const count = document.getElementById("csx-view-count");
    const status = document.getElementById("csx-view-status");
    const label = document.getElementById("csx-view-label");
    const has = sections.length > 0;
    if (prev) prev.disabled = !has;
    if (next) next.disabled = !has;
    if (expMode) expMode.value = state.exportMode || "csv_current";
    const currentNeeded = (state.exportMode === "csv_current" || state.exportMode === "png_current");
    const canExport = currentNeeded ? (has && currentSectionIdx >= 0) : has;
    if (expRun) expRun.disabled = !canExport;
    if (count) count.textContent = `${sections.length} sections`;
    if (status) status.textContent = statusText;
    if (sel) {
      sel.innerHTML = `<option value="">Select section</option>${sections.map((s, i) => `<option value="${i}" ${i === currentSectionIdx ? "selected" : ""}>${esc(s.name)}</option>`).join("")}`;
    }
    if (label) {
      label.textContent = (currentSectionIdx >= 0 && currentSectionIdx < sections.length)
        ? `${sections[currentSectionIdx].name} (${currentSectionIdx + 1}/${sections.length})`
        : "No section selected";
    }
  }

  function setProgress(value01, text = "") {
    progressValue = Math.max(0, Math.min(1, Number(value01) || 0));
    progressText = String(text || `${Math.round(progressValue * 100)}%`);
    const bar = document.getElementById("csx-view-progress-bar");
    const label = document.getElementById("csx-view-progress-label");
    if (bar) bar.style.width = `${Math.round(progressValue * 100)}%`;
    if (label) label.textContent = progressText;
  }

  function ensureHoverMarker() {
    if (hoverMarker) return hoverMarker;
    hoverMarker = L.circleMarker([0, 0], {
      radius: 5,
      color: "#f59e0b",
      fillColor: "#f59e0b",
      fillOpacity: 0.95,
      weight: 2,
      opacity: 1,
    });
    return hoverMarker;
  }

  function clearHoverMarker() {
    if (!hoverMarker) return;
    try { map.removeLayer(hoverMarker); } catch {}
  }

  function applyRootOpacity(opacity01) {
    const o = Math.max(0, Math.min(1, Number(opacity01) || 0));
    globalOpacity = o;
    root.eachLayer((l) => {
      if (l?.setStyle) {
        try {
          const isCenter = l === centerline;
          l.setStyle({
            opacity: isCenter ? Math.max(0.2, o) : Math.max(0.1, 0.9 * o),
          });
        } catch {}
      } else if (l?.setOpacity) {
        try { l.setOpacity(o); } catch {}
      }
    });
  }

  function syncGlobalLayer() {
    if (!globalLayerId || inGlobalSync) return;
    app.services.layers.updateLayer(globalLayerId, {
      name: "Cross Sections",
      visible: globalVisible,
      opacity: globalOpacity,
      group: "Cross Sections",
      meta: {
        featureCount: sections.length + (centerline ? 1 : 0),
        sectionCount: sections.length,
      },
    });
  }

  function ensureGlobalLayer() {
    if (globalLayerId) return;
    globalLayerId = app.services.layers.addLayer("cross-section", {
      name: "Cross Sections",
      kind: "overlay",
      group: "Cross Sections",
      type: "managed",
      visible: globalVisible,
      opacity: globalOpacity,
      volatile: true,
      meta: { featureCount: sections.length + (centerline ? 1 : 0), sectionCount: sections.length },
      managed: {
        setVisible(v) {
          globalVisible = !!v;
          if (globalVisible) root.addTo(map);
          else map.removeLayer(root);
          if (!inGlobalSync) render();
        },
        setOpacity(v) {
          applyRootOpacity(v);
          if (!inGlobalSync) render();
        },
        getBounds() {
          const b = root.getBounds?.();
          return b?.isValid?.() ? b : null;
        },
        bringToFront() {
          try { root.bringToFront?.(); } catch {}
        },
        remove() {
          if (inGlobalSync) return;
          inGlobalSync = true;
          try {
            clearAll();
            globalVisible = false;
            map.removeLayer(root);
            globalLayerId = null;
          } finally {
            inGlobalSync = false;
          }
          render();
        },
      },
    });
  }

  function toTurfLine(latlngs) {
    return turf.lineString(latlngs.map((ll) => [ll.lng, ll.lat]));
  }

  function haversineMeters(a, b) {
    const R = 6371000;
    const toRad = (x) => x * (Math.PI / 180);
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lng - a.lng);
    const la1 = toRad(a.lat);
    const la2 = toRad(b.lat);
    const h = (Math.sin(dLat / 2) ** 2) + (Math.cos(la1) * Math.cos(la2) * (Math.sin(dLon / 2) ** 2));
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  function sampleAlongLine(latlngs, spacingM) {
    const out = [];
    if (!Array.isArray(latlngs) || latlngs.length < 2) return out;
    const spacing = Math.max(1, Number(spacingM) || 1);
    let segStart = latlngs[0];
    let carry = 0;
    for (let i = 1; i < latlngs.length; i += 1) {
      const segEnd = latlngs[i];
      let segLen = haversineMeters(segStart, segEnd);
      while ((carry + segLen) >= spacing) {
        const t = (spacing - carry) / segLen;
        const lat = segStart.lat + (t * (segEnd.lat - segStart.lat));
        const lng = segStart.lng + (t * (segEnd.lng - segStart.lng));
        const p = L.latLng(lat, lng);
        out.push(p);
        segStart = p;
        segLen = haversineMeters(segStart, segEnd);
        carry = 0;
      }
      carry += segLen;
      segStart = segEnd;
    }
    return out;
  }

  function updateSectionStyles() {
    sections.forEach((s, i) => {
      const active = i === currentSectionIdx;
      try {
        s.layer.setStyle({
          color: active ? "#f59e0b" : "#22d3ee",
          weight: active ? 3 : 2,
          opacity: active ? Math.max(0.25, globalOpacity) : Math.max(0.2, 0.8 * globalOpacity),
        });
      } catch {}
    });
  }

  function clearGenerated() {
    sessionToken += 1;
    prefetchRunId += 1;
    pendingProfiles.clear();
    sections.forEach((s) => {
      try { root.removeLayer(s.layer); } catch {}
    });
    sections = [];
    profilesBySectionId = new Map();
    currentSectionIdx = -1;
    setCount();
    setProgress(0, "Idle");
    syncBottomControls();
    syncGlobalLayer();
  }

  function clearAll() {
    sessionToken += 1;
    prefetchRunId += 1;
    pendingProfiles.clear();
    stopDraw(false);
    clearGenerated();
    if (centerline) {
      try { root.removeLayer(centerline); } catch {}
      centerline = null;
    }
    if (profilePlot) {
      try { profilePlot.destroy(); } catch {}
      profilePlot = null;
    }
    if (hoverMarker) {
      try { map.removeLayer(hoverMarker); } catch {}
    }
    if (bottomMounted) renderProfile(null, "");
    setStatus("cleared");
    syncGlobalLayer();
  }

  function drawCenterline() {
    ensureGlobalLayer();
    stopDraw(false);
    setStatus("Draw centerline: click vertices, double-click to finish");
    mapEl.style.cursor = "crosshair";
    const verts = [];
    const tmp = L.polyline([], { color: "#f59e0b", weight: 2, dashArray: "6 4", opacity: Math.max(0.2, globalOpacity) }).addTo(map);
    const dblWas = map.doubleClickZoom?.enabled?.() ?? false;
    map.doubleClickZoom?.disable?.();

    const onClick = (e) => {
      verts.push(e.latlng);
      tmp.setLatLngs(verts);
    };
    const onDbl = (e) => {
      L.DomEvent.stop(e);
      if (verts.length < 2) {
        setStatus("Need at least 2 points");
        return;
      }
      if (centerline) {
        try { root.removeLayer(centerline); } catch {}
      }
      centerline = L.polyline(verts, { color: "#f59e0b", weight: 3, opacity: Math.max(0.25, globalOpacity) }).addTo(root);
      clearGenerated();
      setStatus("Centerline set");
      syncGlobalLayer();
      stopDraw(false);
      render();
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      stopDraw(true);
      setStatus("Draw cancelled");
    };

    drawSession = {
      off() {
        map.off("click", onClick);
        map.off("dblclick", onDbl);
        document.removeEventListener("keydown", onKey);
        if (dblWas) setTimeout(() => map.doubleClickZoom?.enable?.(), 0);
      },
      cleanup() {
        try { map.removeLayer(tmp); } catch {}
      },
    };

    map.on("click", onClick);
    map.on("dblclick", onDbl);
    document.addEventListener("keydown", onKey);
  }

  function stopDraw(resetCursor = true) {
    if (!drawSession) return;
    drawSession.off?.();
    drawSession.cleanup?.();
    drawSession = null;
    if (resetCursor) mapEl.style.cursor = "";
  }

  function buildSections() {
    if (!centerline) {
      setStatus("Draw centerline first");
      return;
    }
    const spacing = Math.max(1, Number(state.sectionSpacingM) || 50);
    const leftM = Math.max(1, Number(state.leftM) || 50);
    const rightM = Math.max(1, Number(state.rightM) || 50);

    clearGenerated();
    const line = toTurfLine(centerline.getLatLngs());
    const lenKm = turf.length(line, { units: "kilometers" });
    const stepKm = spacing / 1000;
    const epsKm = Math.max(0.001, stepKm * 0.05);
    for (let d = 0; d <= lenKm; d += stepKm) {
      const mid = turf.along(line, d, { units: "kilometers" });
      const a = turf.along(line, Math.max(0, d - epsKm), { units: "kilometers" });
      const b = turf.along(line, Math.min(lenKm, d + epsKm), { units: "kilometers" });
      const bearing = turf.bearing(a, b);
      const lpt = turf.destination(mid, leftM / 1000, (bearing - 90 + 360) % 360, { units: "kilometers" });
      const rpt = turf.destination(mid, rightM / 1000, (bearing + 90) % 360, { units: "kilometers" });
      const llA = L.latLng(Number(lpt.geometry.coordinates[1]), Number(lpt.geometry.coordinates[0]));
      const llB = L.latLng(Number(rpt.geometry.coordinates[1]), Number(rpt.geometry.coordinates[0]));
      const idx = sections.length;
      const name = `CS ${String(idx + 1).padStart(3, "0")}`;
      const layer = L.polyline([llA, llB], { color: "#22d3ee", weight: 2, opacity: Math.max(0.2, 0.8 * globalOpacity) }).addTo(root);
      layer.bindTooltip(name);
      layer.on("click", () => { showSection(idx); });
      sections.push({ id: uid("cs"), name, layer, latlngs: [llA, llB] });
    }
    setCount();
    setStatus(`Generated ${sections.length} sections`);
    setProgress(0, "Ready");
    syncGlobalLayer();
    render();
    if (state.autoSampleFirst && sections.length) showSection(0);
  }

  async function sampleSection(section, { silent = false, token = sessionToken } = {}) {
    const pts = sampleAlongLine(section.latlngs, state.sampleSpacingM);
    const out = [];
    let cum = 0;
    for (let i = 0; i < pts.length; i += 1) {
      if (token !== sessionToken) return null;
      const p = pts[i];
      try {
        const r = await app.services.identify.sampleDepth(p.lng, p.lat, "etopo");
        const elevM = Number(r?.depth_m);
        if (!Number.isFinite(elevM)) continue;
        if (state.skipLand && elevM >= 0) continue;
        if (i > 0) cum += haversineMeters(pts[i - 1], p);
        out.push({
          lat: p.lat,
          lng: p.lng,
          dist_m: cum,
          elev_m: elevM,
          depth_m: Math.max(0, -elevM),
        });
      } catch {}
      if (!silent && i % 10 === 0) {
        const p = (i + 1) / Math.max(1, pts.length);
        setStatus(`Sampling ${section.name}`);
        setProgress(p, `Sampling ${section.name} - ${Math.round(p * 100)}%`);
      }
    }
    const totalM = out.length ? out[out.length - 1].dist_m : 0;
    const maxDepthM = out.reduce((m, p) => Math.max(m, p.depth_m), 0);
    return { points: out, total_m: totalM, max_depth_m: maxDepthM };
  }

  async function loadSectionProfile(section, { silent = false, token = sessionToken } = {}) {
    const cached = profilesBySectionId.get(section.id);
    if (cached) return cached;
    const pending = pendingProfiles.get(section.id);
    if (pending) return pending;
    const task = (async () => {
      const profile = await sampleSection(section, { silent, token });
      if (!profile || token !== sessionToken) return null;
      profilesBySectionId.set(section.id, profile);
      return profile;
    })();
    pendingProfiles.set(section.id, task);
    try {
      return await task;
    } finally {
      pendingProfiles.delete(section.id);
    }
  }

  async function startBackgroundPrefetch(startIdx) {
    if (!sections.length) return;
    const thisRun = ++prefetchRunId;
    const token = sessionToken;
    const order = [];
    for (let i = startIdx + 1; i < sections.length; i += 1) order.push(i);
    for (let i = 0; i < startIdx; i += 1) order.push(i);
    for (let n = 0; n < order.length; n += 1) {
      if (thisRun !== prefetchRunId || token !== sessionToken) return;
      const idx = order[n];
      const section = sections[idx];
      if (!section || profilesBySectionId.has(section.id)) continue;
      setProgress(0, `Preloading ${section.name} (${n + 1}/${order.length})`);
      await loadSectionProfile(section, { silent: true, token });
      if (thisRun !== prefetchRunId || token !== sessionToken) return;
      await new Promise((r) => setTimeout(r, 0));
    }
    if (thisRun === prefetchRunId && token === sessionToken) setProgress(1, "Preload complete");
  }

  async function showSection(idx) {
    if (idx < 0 || idx >= sections.length) return;
    currentSectionIdx = idx;
    syncBottomControls();
    updateSectionStyles();
    const section = sections[idx];
    try { map.fitBounds(section.layer.getBounds(), { padding: [20, 20] }); } catch {}
    let profile = profilesBySectionId.get(section.id);
    if (!profile) {
      setStatus(`Sampling ${section.name}...`);
      setProgress(0, `Sampling ${section.name} - 0%`);
      const token = sessionToken;
      profile = await loadSectionProfile(section, { silent: false, token });
      if (!profile || token !== sessionToken) return;
      setStatus(`Done ${section.name}`);
      setProgress(1, `Done ${section.name}`);
    }
    render();
    syncBottomControls();
    renderProfile(profile, section.name);
    startBackgroundPrefetch(idx);
  }

  function renderProfile(profile, label) {
    const mount = document.getElementById("csx-profile");
    if (!mount) return;
    if (profilePlot) {
      try { profilePlot.destroy(); } catch {}
      profilePlot = null;
    }
    if (!profile || !Array.isArray(profile.points) || !profile.points.length) {
      mount.innerHTML = `<div class="small">No profile samples yet.</div>`;
      clearHoverMarker();
      return;
    }
    const total = Math.max(1, Number(profile.total_m) || 1);
    const maxD = Math.max(1, Number(profile.max_depth_m) || 1);
    mount.innerHTML = `
      <div class="small" style="margin-bottom:6px;">${esc(label)} | Length ${(total / 1000).toFixed(2)} km | Max depth ${Math.round(maxD)} m</div>
      <div id="csx-uplot-host" style="width:100%; height:220px;"></div>
      <div id="csx-hover-readout" class="small muted" style="margin-top:4px;">Hover profile to inspect station values</div>
    `;
    const host = mount.querySelector("#csx-uplot-host");
    if (!host) return;
    const xsM = profile.points.map((p) => Number(p.dist_m));
    const ysElev = profile.points.map((p) => Number(p.elev_m));
    const w = Math.max(360, Math.round(host.clientWidth || 760));
    const h = 220;
    const readout = mount.querySelector("#csx-hover-readout");

    profilePlot = new uPlot({
      width: w,
      height: h,
      class: "csx-uplot",
      scales: { x: { time: false } },
      series: [
        { label: "Distance (m)" },
        { label: "Elevation (m)", stroke: "#60a5fa", width: 2, fill: "rgba(96,165,250,0.18)", fillTo: (u, s, min) => min },
      ],
      axes: [
        { label: "Distance (m)", stroke: "#64748b", grid: { stroke: "#1f2937" }, values: (u, vals) => vals.map((v) => `${Math.round(Number(v))}`) },
        { label: "Elevation (m)", stroke: "#64748b", grid: { stroke: "#1f2937" }, values: (u, vals) => vals.map((v) => `${Math.round(Number(v))}`) },
      ],
      cursor: { drag: { x: false, y: false }, points: { show: false } },
      legend: { show: false },
      plugins: [{
        hooks: {
          setCursor: [
            (u) => {
              const i = Number(u?.cursor?.idx);
              if (!Number.isInteger(i) || i < 0 || i >= profile.points.length) {
                clearHoverMarker();
                if (readout) readout.textContent = "Hover profile to inspect station values";
                return;
              }
              const p = profile.points[i];
              const m = ensureHoverMarker();
              m.setLatLng([p.lat, p.lng]);
              if (!map.hasLayer(m)) m.addTo(map);
              if (readout) readout.textContent = `Station ${Math.round(p.dist_m)} m | Depth ${p.depth_m.toFixed(2)} m | Elev ${p.elev_m.toFixed(2)} m`;
            },
          ],
        },
      }],
    }, [xsM, ysElev], host);
  }


  async function profileToPngBlob(profile, sectionName, opts = {}) {
    const mode = opts.mode === "print" ? "print" : "dark";
    const isPrint = mode === "print";
    const palette = isPrint
      ? {
          bg: "#ffffff",
          title: "#111827",
          axis: "#334155",
          grid: "#d1d5db",
          line: "#1d4ed8",
          fill: "rgba(29,78,216,0.12)",
        }
      : {
          bg: "#020617",
          title: "#e5e7eb",
          axis: "#64748b",
          grid: "#1f2937",
          line: "#60a5fa",
          fill: "rgba(96,165,250,0.18)",
        };
    const waitPaint = async () => {
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));
    };
    const mount = document.createElement("div");
    mount.style.position = "fixed";
    mount.style.left = "0";
    mount.style.top = "0";
    mount.style.width = "1200px";
    mount.style.height = "560px";
    mount.style.opacity = "0";
    mount.style.pointerEvents = "none";
    mount.style.zIndex = "-1";
    document.body.appendChild(mount);
    const host = document.createElement("div");
    host.style.width = "1100px";
    host.style.height = "420px";
    host.style.margin = "0";
    mount.appendChild(host);
    const xsM = profile.points.map((p) => Number(p.dist_m));
    const ysElev = profile.points.map((p) => Number(p.elev_m));
    let plot = null;
    try {
      plot = new uPlot({
        width: 1100,
        height: 420,
        class: "csx-uplot",
        scales: { x: { time: false } },
        series: [
          { label: "Distance (m)" },
          { label: "Elevation (m)", stroke: palette.line, width: 2, fill: palette.fill, fillTo: (u, s, min) => min },
        ],
        axes: [
          { label: "Distance (m)", stroke: palette.axis, grid: { stroke: palette.grid }, values: (u, vals) => vals.map((v) => `${Math.round(Number(v))}`) },
          { label: "Elevation (m)", stroke: palette.axis, grid: { stroke: palette.grid }, values: (u, vals) => vals.map((v) => `${Math.round(Number(v))}`) },
        ],
        cursor: { drag: { x: false, y: false }, points: { show: false } },
        legend: { show: false },
      }, [xsM, ysElev], host);
      await waitPaint();
      const canvas = host.querySelector("canvas");
      if (!canvas) return null;
      const out = document.createElement("canvas");
      out.width = 1200;
      out.height = 560;
      const ctx = out.getContext("2d");
      if (!ctx) return null;
      ctx.fillStyle = palette.bg;
      ctx.fillRect(0, 0, out.width, out.height);
      ctx.fillStyle = palette.title;
      ctx.font = "600 24px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";
      ctx.fillText(`Cross Section ${sectionName}`, 24, 36);
      ctx.drawImage(canvas, 50, 90, 1100, 420);
      const format = isPrint ? "image/jpeg" : "image/png";
      const quality = isPrint ? 0.84 : undefined;
      const blob = await new Promise((resolve) => out.toBlob(resolve, format, quality));
      return blob;
    } finally {
      try { plot?.destroy?.(); } catch {}
      try { mount.remove(); } catch {}
    }
  }

  async function exportCurrentPng() {
    if (currentSectionIdx < 0 || currentSectionIdx >= sections.length) {
      setStatus("Select a section first");
      return;
    }
    const sec = sections[currentSectionIdx];
    const profile = profilesBySectionId.get(sec.id) || await loadSectionProfile(sec, { silent: true, token: sessionToken });
    if (!profile?.points?.length) {
      setStatus("No sampled profile to export");
      return;
    }
    setStatus(`Exporting ${sec.name} PNG...`);
    const blob = await profileToPngBlob(profile, sec.name);
    if (!blob) {
      setStatus("PNG export failed");
      return;
    }
    const a = document.createElement("a");
    const u = URL.createObjectURL(blob);
    a.href = u;
    a.download = `${sec.name.replace(/\s+/g, "_").toLowerCase()}.png`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(u); a.remove(); }, 250);
    setStatus(`Exported ${sec.name} PNG`);
  }

  async function exportAllPngZip() {
    if (!sections.length) {
      setStatus("No sections to export");
      return;
    }
    const token = sessionToken;
    const zip = new JSZip();
    setStatus("Preparing PNG exports...");
    setProgress(0, "Exporting 0%");
    for (let i = 0; i < sections.length; i += 1) {
      if (token !== sessionToken) return;
      const sec = sections[i];
      let profile = profilesBySectionId.get(sec.id);
      if (!profile) profile = await loadSectionProfile(sec, { silent: true, token });
      if (!profile?.points?.length) continue;
      const blob = await profileToPngBlob(profile, sec.name);
      if (blob) zip.file(`${sec.name.replace(/\s+/g, "_")}.png`, blob);
      const p = (i + 1) / sections.length;
      setProgress(p, `Exporting ${Math.round(p * 100)}%`);
      setStatus(`Exporting PNGs (${i + 1}/${sections.length})`);
    }
    const zipBlob = await zip.generateAsync({ type: "blob" });
    const a = document.createElement("a");
    const u = URL.createObjectURL(zipBlob);
    a.href = u;
    a.download = `cross_sections_png_${Date.now()}.zip`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(u); a.remove(); }, 250);
    setStatus("Exported PNG ZIP");
    setProgress(1, "Done");
  }

  function runSelectedExport() {
    const mode = state.exportMode || "csv_current";
    if (mode === "csv_current") {
      exportCurrentCsv();
      return;
    }
    if (mode === "png_current") {
      exportCurrentPng();
      return;
    }
    if (mode === "png_all_zip") {
      exportAllPngZip();
      return;
    }
    if (mode === "pdf_all_qc") {
      exportAllPdfQc();
      return;
    }
    exportCurrentCsv();
  }

  function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ""));
      r.onerror = () => reject(r.error || new Error("File read failed"));
      r.readAsDataURL(blob);
    });
  }

  async function exportAllPdfQc() {
    if (!sections.length) {
      setStatus("No sections to export");
      return;
    }
    const token = sessionToken;
    setStatus("Preparing QC PDF...");
    setProgress(0, "Rendering 0%");
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 24;
    const imgW = pageW - (margin * 2);
    const imgH = Math.round((560 / 1200) * imgW);
    const y = 56;
    let pagesAdded = 0;
    for (let i = 0; i < sections.length; i += 1) {
      if (token !== sessionToken) return;
      const sec = sections[i];
      let profile = profilesBySectionId.get(sec.id);
      if (!profile) profile = await loadSectionProfile(sec, { silent: true, token });
      if (!profile?.points?.length) continue;
      const blob = await profileToPngBlob(profile, sec.name, { mode: "print" });
      if (!blob) continue;
      const dataUrl = await blobToDataUrl(blob);
      if (pagesAdded > 0) doc.addPage("letter", "landscape");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text(`Cross Section QC Report - ${sec.name}`, margin, 34);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Section ${i + 1} of ${sections.length}`, pageW - margin, 34, { align: "right" });
      doc.addImage(dataUrl, "JPEG", margin, y, imgW, Math.min(imgH, pageH - y - margin));
      pagesAdded += 1;
      const p = (i + 1) / sections.length;
      setProgress(p, `Rendering ${Math.round(p * 100)}%`);
      setStatus(`Building QC PDF (${i + 1}/${sections.length})`);
    }
    if (!pagesAdded) {
      setStatus("No sampled sections available for PDF");
      setProgress(0, "Idle");
      return;
    }
    doc.save(`cross_sections_qc_${Date.now()}.pdf`);
    setStatus(`Exported QC PDF (${pagesAdded} pages)`);
    setProgress(1, "Done");
  }

  function exportCurrentCsv() {
    if (currentSectionIdx < 0 || currentSectionIdx >= sections.length) {
      setStatus("Select a section first");
      return;
    }
    const sec = sections[currentSectionIdx];
    const profile = profilesBySectionId.get(sec.id);
    if (!profile?.points?.length) {
      setStatus("No sampled profile to export");
      return;
    }
    const rows = [["dist_m", "depth_m", "elev_m", "lat", "lng"]];
    profile.points.forEach((p) => rows.push([p.dist_m, p.depth_m, p.elev_m, p.lat, p.lng]));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const a = document.createElement("a");
    const u = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.href = u;
    a.download = `${sec.name.replace(/\s+/g, "_").toLowerCase()}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(u); a.remove(); }, 250);
  }

  function render() {
    if (!container) return;
    container.innerHTML = `
      <div class="card">
        <div style="font-weight:700;">Cross Section Toolset</div>
        <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap;">
          <label class="small">Section spacing (m)<br><input id="csx-spacing" type="number" min="1" step="1" value="${state.sectionSpacingM}" style="width:110px;"></label>
          <label class="small">Left offset (m)<br><input id="csx-left" type="number" min="1" step="1" value="${state.leftM}" style="width:110px;"></label>
          <label class="small">Right offset (m)<br><input id="csx-right" type="number" min="1" step="1" value="${state.rightM}" style="width:110px;"></label>
          <label class="small">Sample spacing (m)<br><input id="csx-sample" type="number" min="1" step="1" value="${state.sampleSpacingM}" style="width:110px;"></label>
        </div>
        <div class="row" style="margin-top:8px; gap:12px; flex-wrap:wrap;">
          <label class="small"><input id="csx-skip-land" type="checkbox" ${state.skipLand ? "checked" : ""}> Skip land</label>
          <label class="small"><input id="csx-auto" type="checkbox" ${state.autoSampleFirst ? "checked" : ""}> Auto-sample first section</label>
        </div>
        <div class="row" style="margin-top:10px; gap:8px; flex-wrap:wrap;">
          <button id="csx-draw" class="btn primary">Draw centerline</button>
          <button id="csx-generate" class="btn">Generate sections</button>
          <button id="csx-clear" class="btn">Clear</button>
          <span id="csx-status" class="muted" style="margin-left:auto;">idle</span>
        </div>
      </div>
    `;
    setCount();
    syncBottomControls();
  }

  function readUiState() {
    const n = (id, fallback) => {
      const v = Number(container?.querySelector?.(`#${id}`)?.value);
      return Number.isFinite(v) && v > 0 ? v : fallback;
    };
    state.sectionSpacingM = n("csx-spacing", state.sectionSpacingM);
    state.leftM = n("csx-left", state.leftM);
    state.rightM = n("csx-right", state.rightM);
    state.sampleSpacingM = n("csx-sample", state.sampleSpacingM);
    state.skipLand = !!container?.querySelector?.("#csx-skip-land")?.checked;
    state.autoSampleFirst = !!container?.querySelector?.("#csx-auto")?.checked;
  }

  function onClick(e) {
    const id = e.target?.id;
    if (!id) return;
    readUiState();
    if (id === "csx-draw") { drawCenterline(); return; }
    if (id === "csx-generate") { ensureGlobalLayer(); buildSections(); return; }
    if (id === "csx-clear") { clearAll(); render(); return; }
  }

  function onChange(e) {
    const id = e.target?.id;
    if (!id) return;
    readUiState();
    // no-op: section selection controls now live in bottom view
  }

  function mount(next) {
    if (container === next) return;
    container = next;
    ensureBottomPanel();
    render();
    container.addEventListener("click", onClick);
    container.addEventListener("change", onChange);
    syncGlobalLayer();
  }

  function snapshot() {
    return {
      state: { ...state },
      centerline: centerline ? centerline.getLatLngs().map((ll) => [ll.lat, ll.lng]) : null,
      currentSectionIdx,
    };
  }

  function restore(data) {
    clearAll();
    if (bottomMounted) {
      app.ui.unmountBottomPanel("cross-section");
      bottomMounted = false;
    }
    if (!data || typeof data !== "object") return;
    if (data.state && typeof data.state === "object") {
      state.sectionSpacingM = Math.max(1, Number(data.state.sectionSpacingM) || state.sectionSpacingM);
      state.leftM = Math.max(1, Number(data.state.leftM) || state.leftM);
      state.rightM = Math.max(1, Number(data.state.rightM) || state.rightM);
      state.sampleSpacingM = Math.max(1, Number(data.state.sampleSpacingM) || state.sampleSpacingM);
      state.skipLand = data.state.skipLand !== false;
      state.autoSampleFirst = data.state.autoSampleFirst !== false;
      state.exportMode = typeof data.state.exportMode === "string" ? data.state.exportMode : "csv_current";
    }
    const cl = Array.isArray(data.centerline) ? data.centerline : null;
    if (cl && cl.length >= 2) {
      centerline = L.polyline(cl.map(([lat, lng]) => L.latLng(Number(lat), Number(lng))), { color: "#f59e0b", weight: 3, opacity: Math.max(0.25, globalOpacity) }).addTo(root);
      buildSections();
      const i = Number(data.currentSectionIdx);
      if (Number.isInteger(i) && i >= 0 && i < sections.length) {
        currentSectionIdx = i;
        updateSectionStyles();
      }
    }
    render();
    syncGlobalLayer();
  }

  function destroy() {
    stopDraw(false);
    if (container) {
      container.removeEventListener("click", onClick);
      container.removeEventListener("change", onChange);
    }
    clearAll();
    if (globalLayerId) {
      inGlobalSync = true;
      try { app.services.layers.removeLayer(globalLayerId); } catch {}
      finally { inGlobalSync = false; }
      globalLayerId = null;
    }
    try { map.removeLayer(root); } catch {}
    if (hoverMarker) {
      try { map.removeLayer(hoverMarker); } catch {}
    }
    container = null;
  }

  return { mount, snapshot, restore, destroy };
}
